import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Inbox as InboxIcon, Star, Send, Archive, Trash2, AlertCircle, FileEdit,
  Search, Sparkles, Reply, PenSquare, RefreshCw, Bot, User, FolderPlus, Folder, X, Plus, MoreVertical, Pencil, FolderInput,
} from "lucide-react";
import Composer from "@/components/Composer";
import SenderDetailsPanel from "@/components/SenderDetailsPanel";

const SYSTEM_FOLDERS = [
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "starred", label: "Starred", icon: Star, filter: { starred: true } },
  { key: "assigned-to-me", label: "Assigned to me", icon: User, filter: { assigned_to_me: true } },
  { key: "sent", label: "Sent", icon: Send, filter: { folder: "sent" } },
  { key: "drafts", label: "Drafts", icon: FileEdit, filter: { folder: "drafts" } },
  { key: "archive", label: "Archive", icon: Archive, filter: { folder: "archive" } },
  { key: "spam", label: "Spam", icon: AlertCircle, filter: { folder: "spam" } },
  { key: "trash", label: "Trash", icon: Trash2, filter: { folder: "trash" } },
];

const FOLDER_COLORS = [
  { key: "emerald", chip: "bg-emerald-500", side: "text-emerald-600 border-emerald-500/40" },
  { key: "sky", chip: "bg-sky-500", side: "text-sky-600 border-sky-500/40" },
  { key: "amber", chip: "bg-amber-500", side: "text-amber-600 border-amber-500/40" },
  { key: "rose", chip: "bg-rose-500", side: "text-rose-600 border-rose-500/40" },
  { key: "violet", chip: "bg-violet-500", side: "text-violet-600 border-violet-500/40" },
  { key: "terracotta", chip: "bg-terracotta", side: "text-terracotta border-terracotta/40" },
  { key: "slate", chip: "bg-slate-400", side: "text-slate-600 border-slate-400/40" },
];
const colorChip = (c) => FOLDER_COLORS.find((x) => x.key === c)?.chip || "bg-slate-400";

const TABS_STORAGE_KEY = "relay.inbox.mailboxTabs";

export default function Inbox() {
  const { folder: folderParam } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId } = useAuth();

  const folderKey = folderParam || "inbox";
  const isCustomFolder = folderKey.startsWith("cf-");
  const customFolderId = isCustomFolder ? folderKey.slice(3) : null;
  const systemFolder = SYSTEM_FOLDERS.find((f) => f.key === folderKey) || SYSTEM_FOLDERS[0];

  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerPrefill, setComposerPrefill] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [customFolders, setCustomFolders] = useState([]);
  const [folderDialog, setFolderDialog] = useState(null); // { edit?, name, color, accountId }

  // Multi-account tabs — restore from localStorage
  const [tabs, setTabs] = useState(() => {
    try {
      const raw = localStorage.getItem(TABS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [activeTab, setActiveTab] = useState(() => tabs[0] || "");
  const accountId = activeTab || "";

  useEffect(() => {
    try { localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(tabs)); } catch { /* ignore */ }
  }, [tabs]);

  useEffect(() => {
    (async () => {
      try {
        const params = activeCompanyId ? { company_id: activeCompanyId } : {};
        const { data } = await api.get("/email-accounts", { params });
        setAccounts(data);
        // If no active tab yet and we have accounts, seed with the first
        if (!activeTab && data.length && tabs.length === 0) {
          setTabs([data[0].id]);
          setActiveTab(data[0].id);
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [activeCompanyId]);

  const loadCustomFolders = async () => {
    if (!accountId) { setCustomFolders([]); return; }
    try {
      const { data } = await api.get("/mail-folders", { params: { account_id: accountId } });
      setCustomFolders(data);
    } catch { setCustomFolders([]); }
  };
  useEffect(() => { loadCustomFolders(); /* eslint-disable-next-line */ }, [accountId]);

  const params = useMemo(() => {
    const p = { ...(activeCompanyId ? { company_id: activeCompanyId } : {}) };
    if (accountId) p.account_id = accountId;
    if (isCustomFolder && customFolderId) p.custom_folder_id = customFolderId;
    else if (systemFolder.filter) Object.assign(p, systemFolder.filter);
    else if (folderKey === "inbox") p.folder = "inbox";
    if (q) p.q = q;
    return p;
  }, [activeCompanyId, accountId, folderKey, isCustomFolder, customFolderId, systemFolder, q]);

  const loadList = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: cnt }] = await Promise.all([
        api.get("/emails", { params }),
        api.get("/emails/counts", { params: { ...(activeCompanyId ? { company_id: activeCompanyId } : {}), ...(accountId ? { account_id: accountId } : {}) } }),
      ]);
      setEmails(list);
      setCounts(cnt);
      if (list.length && !list.find((x) => x.id === selected?.id)) {
        openEmail(list[0].id);
      } else if (list.length === 0) {
        setSelected(null); setThread([]);
      }
    } catch { toast.error("Failed to load emails"); }
    setLoading(false);
  };

  const openEmail = async (id) => {
    try {
      const { data } = await api.get(`/emails/${id}`);
      setSelected(data);
      if (data.thread_id) {
        const { data: tr } = await api.get(`/emails/thread/${data.thread_id}`);
        setThread(tr);
      } else setThread([data]);
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [params]);

  const syncAll = async () => {
    try {
      const list = accountId ? accounts.filter((a) => a.id === accountId) : accounts;
      if (list.length === 0) {
        toast.error("No email accounts configured yet.");
        return;
      }
      toast.info(`Syncing ${list.length} mailbox${list.length > 1 ? "es" : ""}…`);
      const results = await Promise.all(list.map((a) => api.post(`/email-accounts/${a.id}/sync`).catch(() => ({ data: { errors: 1 } }))));
      const newCount = results.reduce((s, r) => s + (r.data?.new || 0), 0);
      toast.success(`Sync complete — ${newCount} new`);
      loadList();
    } catch { toast.error("Sync failed"); }
  };

  const runAgent = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post("/ai/agent/run", { email_id: selected.id });
      toast.success(`Agent: ${data.status}`);
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Agent failed"); }
  };

  const setEmailField = async (updates) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/emails/${selected.id}`, updates);
      setSelected(data); loadList();
    } catch { toast.error("Failed"); }
  };

  const moveEmailToFolder = async (email, folderId) => {
    try {
      await api.post(`/mail-folders/${folderId}/move-email/${email.id}`);
      toast.success("Moved");
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  const openReply = () => {
    if (!selected) return;
    setComposerPrefill({
      account_id: selected.account_id,
      to: [selected.from_email],
      subject: selected.subject.startsWith("Re:") ? selected.subject : `Re: ${selected.subject}`,
      in_reply_to_message_id: selected.id,
      body_text: "",
    });
    setComposerOpen(true);
  };

  const submitFolder = async () => {
    if (!folderDialog?.name?.trim()) { toast.error("Name required"); return; }
    try {
      if (folderDialog.edit) {
        await api.patch(`/mail-folders/${folderDialog.edit.id}`, { name: folderDialog.name, color: folderDialog.color });
        toast.success("Folder updated");
      } else {
        const acct = accounts.find((a) => a.id === folderDialog.accountId);
        await api.post("/mail-folders", {
          company_id: acct?.company_id, account_id: folderDialog.accountId,
          name: folderDialog.name, color: folderDialog.color,
        });
        toast.success("Folder created");
      }
      setFolderDialog(null);
      loadCustomFolders();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm("Delete this folder? Emails inside will move back to Inbox.")) return;
    try {
      await api.delete(`/mail-folders/${id}`);
      toast.success("Folder removed");
      if (isCustomFolder && customFolderId === id) navigate("/inbox");
      loadCustomFolders();
    } catch { toast.error("Failed"); }
  };

  const addTab = (id) => {
    if (tabs.includes(id)) { setActiveTab(id); return; }
    if (tabs.length >= 4) { toast.error("Max 4 mailbox tabs — close one first."); return; }
    const next = [...tabs, id];
    setTabs(next); setActiveTab(id);
  };
  const closeTab = (id) => {
    const next = tabs.filter((t) => t !== id);
    setTabs(next);
    if (activeTab === id) setActiveTab(next[0] || "");
  };

  const availableToAdd = accounts.filter((a) => !tabs.includes(a.id));

  return (
    <div className="flex h-screen overflow-hidden" data-testid="inbox-page">
      {/* Folder rail */}
      <div className="w-56 border-r border-border flex flex-col">
        <div className="p-3 border-b border-border">
          <Button className="w-full rounded-sm" onClick={() => { setComposerPrefill(null); setComposerOpen(true); }} data-testid="compose-button">
            <PenSquare className="h-4 w-4 mr-1.5" /> Compose
          </Button>
        </div>

        <div className="p-2 space-y-0.5 flex-1 overflow-y-auto">
          {SYSTEM_FOLDERS.map((f) => (
            <button
              key={f.key}
              onClick={() => navigate(`/inbox/${f.key === "inbox" ? "" : f.key}`.replace(/\/$/, "/inbox"))}
              className={`side-link w-full ${folderKey === f.key ? "active" : ""}`}
              data-testid={`folder-${f.key}`}
            >
              <f.icon className="h-4 w-4" strokeWidth={1.75} />
              <span className="flex-1 text-left">{f.label}</span>
              {counts[f.key === "starred" ? "starred" : f.key === "assigned-to-me" ? "assigned_to_me" : f.key] > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {counts[f.key === "starred" ? "starred" : f.key === "assigned-to-me" ? "assigned_to_me" : f.key]}
                </span>
              )}
            </button>
          ))}

          {/* Custom folders per active tab */}
          <div className="mt-3 mb-1 flex items-center justify-between px-2">
            <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Folders</span>
            <button
              type="button"
              disabled={!accountId}
              onClick={() => setFolderDialog({ name: "", color: "emerald", accountId })}
              className="text-muted-foreground hover:text-primary disabled:opacity-40"
              data-testid="new-folder-button"
              title={accountId ? "Create folder" : "Open a mailbox tab first"}
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </button>
          </div>
          {!accountId && (
            <div className="text-[10px] text-muted-foreground px-2">Open a mailbox tab to see its folders.</div>
          )}
          {customFolders.map((cf) => (
            <div key={cf.id} className="group flex items-center">
              <button
                onClick={() => navigate(`/inbox/cf-${cf.id}`)}
                className={`side-link flex-1 ${folderKey === `cf-${cf.id}` ? "active" : ""}`}
                data-testid={`custom-folder-${cf.id}`}
              >
                <span className={`h-2.5 w-2.5 rounded-sm ${colorChip(cf.color)}`} />
                <span className="flex-1 text-left truncate">{cf.name}</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="px-1.5 opacity-0 group-hover:opacity-100 text-muted-foreground" data-testid={`custom-folder-menu-${cf.id}`}><MoreVertical className="h-3.5 w-3.5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 rounded-xl">
                  <DropdownMenuItem onClick={() => setFolderDialog({ edit: cf, name: cf.name, color: cf.color, accountId: cf.account_id })}>
                    <Pencil className="h-3.5 w-3.5 mr-2" /> Rename / recolor
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => deleteFolder(cf.id)} className="text-crimson focus:text-crimson">
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={syncAll} className="w-full rounded-sm" data-testid="sync-all-button">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sync all folders
          </Button>
        </div>
      </div>

      {/* Middle column: tabs + list */}
      <div className="w-80 border-r border-border flex flex-col">
        {/* Mailbox tabs strip */}
        <div className="flex items-stretch border-b border-border overflow-x-auto" data-testid="mailbox-tabs">
          {tabs.map((tid) => {
            const acct = accounts.find((a) => a.id === tid);
            if (!acct) return null;
            const active = activeTab === tid;
            return (
              <div
                key={tid}
                className={`group flex items-center gap-1 px-2 py-1.5 text-xs cursor-pointer border-r border-border min-w-[128px] ${active ? "bg-emerald-500/10 text-emerald-700 border-b-2 border-b-emerald-500" : "text-muted-foreground hover:bg-muted/50"}`}
                onClick={() => setActiveTab(tid)}
                data-testid={`mailbox-tab-${tid}`}
              >
                <div className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                <span className="truncate flex-1" title={acct.email_address}>{acct.email_address.split("@")[0]}</span>
                <button onClick={(e) => { e.stopPropagation(); closeTab(tid); }} className="opacity-0 group-hover:opacity-100" data-testid={`close-tab-${tid}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          {availableToAdd.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-2 text-muted-foreground hover:text-foreground" data-testid="add-mailbox-tab" title="Add mailbox tab">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl w-56">
                <DropdownMenuLabel className="text-[10px] font-mono uppercase text-muted-foreground">Add mailbox tab</DropdownMenuLabel>
                {availableToAdd.map((a) => (
                  <DropdownMenuItem key={a.id} onClick={() => addTab(a.id)} data-testid={`add-tab-${a.id}`}>
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-2" />
                    <span className="truncate">{a.email_address}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="p-3 border-b border-border flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 rounded-sm border-0 shadow-none focus-visible:ring-0 px-0" data-testid="inbox-search" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && emails.length === 0 && Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="p-3 border-b border-border">
              <div className="ai-shimmer h-3 w-3/4 rounded-sm mb-2" />
              <div className="ai-shimmer h-2 w-full rounded-sm" />
            </div>
          ))}
          {emails.map((e) => {
            const unread = !e.read;
            return (
              <div
                key={e.id}
                className={`group w-full text-left p-3 border-b border-border email-row border-l-4 cursor-pointer ${
                  selected?.id === e.id ? "active" : ""
                } ${unread ? "border-l-emerald-500 bg-emerald-500/[0.06]" : "border-l-transparent"}`}
                onClick={() => openEmail(e.id)}
                data-testid={`email-row-${e.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className={`h-1.5 w-1.5 rounded-full ${unread ? "bg-emerald-500" : "bg-transparent"}`} />
                      <span className={`text-sm truncate ${unread ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
                        {e.from_name || e.from_email}
                      </span>
                    </div>
                    <div className={`text-xs mt-0.5 truncate ${unread ? "text-foreground font-medium" : "text-muted-foreground"}`}>{e.subject || "(no subject)"}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{e.preview}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="font-mono text-[10px] text-muted-foreground">{(e.received_at || "").slice(11, 16)}</div>
                    <div className="flex items-center gap-1">
                      {e.starred && <Star className="h-3.5 w-3.5" style={{ color: "#1d4ed8", fill: "#1d4ed8" }} data-testid={`star-marker-${e.id}`} />}
                      {e.ai_handled && <Bot className="h-3 w-3 text-terracotta" />}
                    </div>
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(e.priority === "high" || e.priority === "urgent") && (
                      <Badge className={`text-[9px] font-mono uppercase tracking-widest rounded-sm ${e.priority === "urgent" ? "bg-destructive" : "bg-terracotta"}`}>{e.priority}</Badge>
                    )}
                    {e.custom_folder_id && customFolders.find((f) => f.id === e.custom_folder_id) && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                        <span className={`h-1.5 w-1.5 rounded-sm ${colorChip(customFolders.find((f) => f.id === e.custom_folder_id).color)}`} />
                        {customFolders.find((f) => f.id === e.custom_folder_id).name}
                      </span>
                    )}
                  </div>
                  {customFolders.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(ev) => ev.stopPropagation()}>
                        <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" data-testid={`move-menu-${e.id}`}>
                          <FolderInput className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl w-52" onClick={(ev) => ev.stopPropagation()}>
                        <DropdownMenuLabel className="text-[10px] font-mono uppercase">Move to folder</DropdownMenuLabel>
                        {customFolders.map((cf) => (
                          <DropdownMenuItem key={cf.id} onClick={() => moveEmailToFolder(e, cf.id)} data-testid={`move-to-${cf.id}-${e.id}`}>
                            <span className={`h-2 w-2 rounded-sm mr-2 ${colorChip(cf.color)}`} />
                            {cf.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && emails.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Empty. Try syncing your mailbox.
            </div>
          )}
        </div>
      </div>

      {/* Thread view */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selected ? (
          <>
            <div className="border-b border-border p-4 lg:p-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-display text-xl font-semibold tracking-tight truncate">{selected.subject || "(no subject)"}</h2>
                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                  {selected.category && <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{selected.category}</Badge>}
                  {selected.priority && <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{selected.priority}</Badge>}
                  {selected.ai_sentiment && <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{selected.ai_sentiment}</Badge>}
                  {selected.ai_confidence > 0 && <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase text-terracotta border-terracotta">AI {Math.round(selected.ai_confidence * 100)}%</Badge>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEmailField({ starred: !selected.starred })} data-testid="star-button">
                  <Star className="h-4 w-4" style={selected.starred ? { color: "#1d4ed8", fill: "#1d4ed8" } : {}} />
                </Button>
                {customFolders.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="rounded-sm" data-testid="header-move-button" title="Move to folder">
                        <FolderInput className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl w-52">
                      <DropdownMenuLabel className="text-[10px] font-mono uppercase">Move to folder</DropdownMenuLabel>
                      {customFolders.map((cf) => (
                        <DropdownMenuItem key={cf.id} onClick={() => moveEmailToFolder(selected, cf.id)} data-testid={`header-move-${cf.id}`}>
                          <span className={`h-2 w-2 rounded-sm mr-2 ${colorChip(cf.color)}`} />
                          {cf.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEmailField({ folder: "archive" })} data-testid="archive-button">
                  <Archive className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEmailField({ folder: "trash" })} data-testid="trash-button">
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button size="sm" className="rounded-sm" onClick={openReply} data-testid="reply-button">
                  <Reply className="h-4 w-4 mr-1.5" /> Reply
                </Button>
              </div>
            </div>

            {selected.ai_summary && (
              <div className="mx-4 lg:mx-5 mt-4 border-t-2 border-terracotta bg-card rounded-sm p-4" data-testid="ai-summary-card">
                <div className="text-[10px] font-mono uppercase tracking-widest text-terracotta flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI summary</div>
                <p className="text-sm mt-1.5">{selected.ai_summary}</p>
                {selected.ai_recommended_action && (
                  <p className="text-xs text-muted-foreground mt-2"><span className="font-mono uppercase text-[10px]">Action:</span> {selected.ai_recommended_action}</p>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={runAgent} className="rounded-sm text-xs" data-testid="run-agent-button">
                    <Bot className="h-3.5 w-3.5 mr-1.5" /> Run department agent
                  </Button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 lg:p-5 space-y-3">
              {thread.map((m) => (
                <div key={m.id} className="border border-border rounded-sm">
                  <div className="p-3 border-b border-border flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className={`h-6 w-6 rounded-sm grid place-items-center ${m.direction === "outbound" ? "bg-primary/15 text-primary" : "bg-terracotta/15 text-terracotta"}`}>
                        {m.direction === "outbound" ? <Send className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      </div>
                      <div>
                        <div className="font-medium">{m.from_name || m.from_email}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">to {m.to?.join(", ")}</div>
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">{(m.received_at || m.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="px-4 pt-3">
                    <SenderDetailsPanel emailId={m.id} />
                  </div>
                  <div className="p-4 text-sm whitespace-pre-wrap leading-relaxed">
                    {m.body_html ? (
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: m.body_html }} />
                    ) : (
                      m.body_text || m.preview
                    )}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                        {m.attachments.map((att, ai) => (
                          <a
                            key={ai}
                            href={att.storage_id ? `${process.env.REACT_APP_BACKEND_URL}/api/attachments/${att.storage_id}` : "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 border border-border rounded-lg px-2.5 py-1.5 bg-muted/40 text-xs hover:bg-muted"
                          >
                            <span className="font-mono">{att.filename}</span>
                            <span className="text-[10px] text-muted-foreground">{Math.round((att.size || 0) / 1024)}KB</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {thread.length === 0 && <div className="text-muted-foreground text-sm">No messages in thread.</div>}
            </div>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
            Select an email to view.
          </div>
        )}
      </div>

      <Composer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        prefill={composerPrefill}
        onSent={() => loadList()}
      />

      {/* Folder create/rename dialog */}
      <Dialog open={!!folderDialog} onOpenChange={(o) => !o && setFolderDialog(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <Folder className="h-4 w-4" /> {folderDialog?.edit ? "Rename folder" : "New folder"}
            </DialogTitle>
          </DialogHeader>
          {folderDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input className="mt-1.5 rounded-sm" value={folderDialog.name} onChange={(e) => setFolderDialog({ ...folderDialog, name: e.target.value })} placeholder="Follow-up" data-testid="folder-name-input" autoFocus />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Color</Label>
                <div className="grid grid-cols-7 gap-2 mt-1.5">
                  {FOLDER_COLORS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setFolderDialog({ ...folderDialog, color: c.key })}
                      className={`h-8 rounded-sm ${c.chip} ring-offset-2 ${folderDialog.color === c.key ? "ring-2 ring-foreground" : ""}`}
                      data-testid={`folder-color-${c.key}`}
                      aria-label={c.key}
                    />
                  ))}
                </div>
              </div>
              {!folderDialog.edit && (
                <div className="text-[10px] text-muted-foreground font-mono">
                  Folder will be created inside {accounts.find((a) => a.id === folderDialog.accountId)?.email_address}.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setFolderDialog(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={submitFolder} data-testid="folder-save">{folderDialog?.edit ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
